import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import FreeShippingProgress, { FREE_TWO_DAY_THRESHOLD } from '../../components/FreeShippingProgress';


/** Line items + subtotal preview. Read-only, no business logic. */
export function OrderSummaryCard({ items }) {
  return (
    <Card>
      <CardHeader><CardTitle className="font-heading text-lg">Order Summary</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {items.map((item) => (
          <div key={`${item.product_id}-${item.variant || ''}`} className="flex gap-4 py-2">
            <div className="w-12 h-12 rounded bg-secondary/20 overflow-hidden flex-shrink-0">
              <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{item.name}</p>
              {item.variant && <p className="text-xs text-muted-foreground">{item.variant}</p>}
              <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
            </div>
            <p className="text-sm font-semibold">${item.subtotal.toFixed(2)}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}


/** Shipping picker. Hides the "Free Standard" option when 2-Day also qualifies. */
export function ShippingMethodCard({
  discountedSubtotal, shippingOptions, selectedShipping, setSelectedShipping, effectiveOptionRate,
}) {
  // When 2-Day shipping qualifies for FREE, hide the Free Standard option
  // entirely — customers always prefer faster + free.
  const twoDayFree = discountedSubtotal >= FREE_TWO_DAY_THRESHOLD;
  const visibleOptions = twoDayFree
    ? shippingOptions.filter((o) => o.service_code !== 'standard')
    : shippingOptions;

  return (
    <Card data-testid="shipping-method-card">
      <CardHeader>
        <CardTitle className="font-heading text-lg">Shipping Method</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FreeShippingProgress subtotal={discountedSubtotal} />
        {discountedSubtotal <= 0 ? (
          <p className="text-sm text-green-600 font-medium">
            Your order qualifies for free shipping (100% promo applied).
          </p>
        ) : (
          <div className="space-y-2" data-testid="shipping-options">
            {visibleOptions.map((opt) => (
              <ShippingOption
                key={opt.service_code}
                opt={opt}
                isSelected={selectedShipping?.service_code === opt.service_code}
                effectiveRate={effectiveOptionRate(opt)}
                onSelect={() => setSelectedShipping(opt)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function ShippingOption({ opt, isSelected, effectiveRate, onSelect }) {
  const id = `ship-opt-${opt.service_code}`;
  const isFree = effectiveRate === 0;
  return (
    <label
      htmlFor={id}
      className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
        isSelected ? 'border-[hsl(210,45%,40%)] bg-[hsl(210,45%,95%)]' : 'border-border hover:bg-muted/30'
      }`}
      data-testid={`shipping-option-${opt.service_code}`}
    >
      <span
        className={`flex h-4 w-4 items-center justify-center rounded-full border-2 flex-shrink-0 ${
          isSelected ? 'border-[hsl(210,45%,40%)]' : 'border-muted-foreground/40'
        }`}
        aria-hidden="true"
      >
        {isSelected && <span className="h-2 w-2 rounded-full bg-[hsl(210,45%,40%)]" />}
      </span>
      <input
        type="radio" id={id} name="shipping-option"
        checked={isSelected} onChange={onSelect}
        className="sr-only"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">
          {isFree ? `Free ${opt.service_name}` : opt.service_name}
        </p>
        <p className="text-xs text-muted-foreground">{opt.transit_time}</p>
      </div>
      {isFree
        ? <span className="text-sm font-semibold whitespace-nowrap text-green-600">FREE</span>
        : <span className="text-sm font-semibold whitespace-nowrap">${opt.rate.toFixed(2)}</span>}
    </label>
  );
}
